import type { CounterId, GameState } from '../state/types';
import type { SpotId } from '../world/layout';
import type { IconToken } from '../icons/index';

/**
 * Every way to make a buck in Lucky Acres, as a ladder: quick pocket change you can do
 * right where you're standing, side hustles, and big schemes that take days. The new
 * ones unlock by doing the ones below them. (Your phone's Hustles tab shows it.)
 */
export type HustleId =
  'favors' | 'carwash' | 'cans' | 'oddjobs' | 'delivery' | 'backforty' | 'shinerun' | 'landlord';

export type HustleTier = 1 | 2 | 3;

export interface HustleNeed {
  label: string;
  have: (s: GameState) => number;
  need: number;
}

export interface HustleDef {
  id: HustleId;
  tier: HustleTier;
  name: string;
  icon: IconToken;
  /** What it pays and how long it takes, roughly. */
  pay: string;
  time: string;
  /** What it is, and how to get started. */
  blurb: string;
  how: string;
  /** Where "Show me" points: a spot, your own lot, or Lot 10. None for favors (they find you). */
  pin?: SpotId | 'home' | 'lot10';
  /** Everything it takes to unlock (none: open from the start). */
  needs: readonly HustleNeed[];
  /** How many times you've done it. */
  done: (s: GameState) => number;
  /** Brand new this season: gets a NEW badge until you've done it once. */
  fresh?: boolean;
}

const c = (s: GameState, id: CounterId): number => s.counters[id] ?? 0;
const quick = (s: GameState): number => c(s, 'favors') + c(s, 'carWashes');

export const TIERS: Record<HustleTier, { name: string; blurb: string }> = {
  1: { name: ':coin: Pocket Change', blurb: 'Quick, close by, a few bucks at a time.' },
  2: { name: ':cash: Side Hustles', blurb: 'More work, more money. Some of it legal.' },
  3: { name: ':moneybag: Big Schemes', blurb: 'Complicated, slow, and worth it.' },
};

export const HUSTLES: readonly HustleDef[] = [
  {
    id: 'favors',
    tier: 1,
    name: 'Favors for the Neighbors',
    icon: ':raise-hand:',
    pay: '$3–12',
    time: 'a minute',
    blurb:
      "Somebody's always waving you over: fetch a cold one, find Jenkins' teeth, carry Meemaw's groceries, run a casserole across the park.",
    how: 'Look for a :dollar: over somebody nearby and talk to them. Eight a day.',
    needs: [],
    done: (s) => c(s, 'favors'),
    fresh: true,
  },
  {
    id: 'carwash',
    tier: 1,
    name: 'Driveway Car Wash',
    icon: ':sponge:',
    pay: '$6–12 a car',
    time: 'a minute a car',
    blurb:
      'Hang the sign, grab the bucket, and the neighbors pull up out front. Scrub off every splatter of mud and they pay up (faster means a tip).',
    how: 'The CAR WASH sign by your mailbox, 9 AM to 7 PM. Six cars a day.',
    pin: 'home',
    needs: [],
    done: (s) => c(s, 'carWashes'),
    fresh: true,
  },
  {
    id: 'cans',
    tier: 1,
    name: 'Cans & Scrap',
    icon: ':empty-can:',
    pay: '25¢–$2 apiece',
    time: 'whenever',
    blurb:
      "Walk over anything that sparkles. Junkyard Jerry buys it all, and he doesn't ask where it's been.",
    how: "Sell to Junkyard Jerry's window, north end of the park.",
    pin: 'junkyardWindow',
    needs: [],
    done: (s) => c(s, 'cansRecycled') + c(s, 'scrapSold'),
  },
  {
    id: 'oddjobs',
    tier: 2,
    name: 'Odd Jobs',
    icon: ':sprout:',
    pay: '$30–65',
    time: 'an hour or two',
    blurb: 'Push-mow a lawn blade by blade, or haul a haunted recliner to the junkyard.',
    how: 'The job board at Suds-N-Duds, across the highway. Two a day.',
    pin: 'jobBoard',
    needs: [],
    done: (s) => c(s, 'jobs'),
  },
  {
    id: 'backforty',
    tier: 2,
    name: 'Back Forty Money',
    icon: ':catfish:',
    pay: '$15–50',
    time: 'an hour',
    blurb:
      "Noodle catfish for Big Earl, strip copper out of the condemned trailers, run Uncle Rudy's still for Trenchcoat Terry, hustle Lil' Ricky at cornhole.",
    how: 'North end of Possum Trot Lane. Lock your car.',
    pin: 'backFortySign',
    needs: [],
    done: (s) => c(s, 'catfish') + c(s, 'copperSold') + c(s, 'shineSold') + c(s, 'cornholeWins'),
  },
  {
    id: 'delivery',
    tier: 2,
    name: 'Hot Case Delivery',
    icon: ':pizza:',
    pay: '$25–50 a run',
    time: 'a few minutes',
    blurb:
      "Raylene's got orders all over the park: pizza, a twelve-piece bucket, gas station sushi somebody actually asked for. Three stops a run, and the tip goes cold with the food. Wheels help.",
    how: 'Talk to Raylene inside the Gas-N-Grub, 10 AM to 11 PM. Three runs a day.',
    pin: 'gasDoor',
    needs: [{ label: 'Pocket-change hustles done (favors or car washes)', have: quick, need: 4 }],
    done: (s) => c(s, 'deliveries'),
    fresh: true,
  },
  {
    id: 'shinerun',
    tier: 3,
    name: 'Shine Runs',
    icon: ':moonshine:',
    pay: '$45 a jar',
    time: 'a night',
    blurb:
      "Terry's got customers across the county who want your shine, delivered, after dark. Twice what he pays at the barrels. Deputy Boyd patrols the park roads with a spotlight. Don't get lit up.",
    how: 'Talk to Trenchcoat Terry at the burn barrels after 7 PM, with at least 2 jars of your own shine.',
    pin: 'terry',
    needs: [
      { label: 'Jars sold to Trenchcoat Terry', have: (s) => c(s, 'shineSold'), need: 3 },
      { label: 'Delivery runs for Raylene', have: (s) => c(s, 'deliveries'), need: 2 },
    ],
    done: (s) => c(s, 'shineRuns'),
    fresh: true,
  },
  {
    id: 'landlord',
    tier: 3,
    name: 'Landlord of Lot 10',
    icon: ':shack:',
    pay: '$120 a week, or ~$2,000',
    time: 'a few days',
    blurb:
      'Lease the burnt-out lot across from Jenkins, haul off the wreck, buy a repo single-wide from Big Dave, fix it up (roof, windows, steps, skirting, paint) and rent it out every Friday. Or flip it.',
    how: 'Ask Darlene about Lot 10 (she hangs around the park office). About $1,000 to get going.',
    pin: 'lot10',
    needs: [
      { label: 'Odd jobs done', have: (s) => c(s, 'jobs'), need: 3 },
      { label: 'Delivery runs for Raylene', have: (s) => c(s, 'deliveries'), need: 3 },
    ],
    done: (s) => c(s, 'flips') + (s.flags.rental_stage === 'rented' ? 1 : 0),
    fresh: true,
  },
];

export const hustleDef = (id: HustleId): HustleDef => HUSTLES.find((h) => h.id === id) as HustleDef;

/** Is a hustle open yet? */
export function hustleOpen(s: GameState, id: HustleId): boolean {
  return hustleDef(id).needs.every((n) => n.have(s) >= n.need);
}
